import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { PageHeader, Pager, StatusBadge } from '@/components/kit';
import { FilterSelect, ListBody, SearchBox, Stars, Toolbar, useDebounced } from '@/components/inputs';
import DataTable, { Dash, RowLink } from '@/components/admin/DataTable';
import { PLAN_LABELS, VISIBILITY_STYLES } from '@/components/admin/ReviewForm';
import { REVIEW_FIELDS } from '@/shared/fields';
import { idOf, paged } from '@/shared/data';
import { PLAN_VALUES } from '@/shared/pricing';
import { formatRelativeTime } from '@/utils/date';

const { eq, or, contains } = window.ApperSDK.CoreSDK;

export const route = { path: '/admin/reviews', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };
export const nav = { icon: 'Star', label: 'Reviews', section: 'Support', profiles: ['Admin_c', 'Staff_c'], order: 60 };

const PAGE_SIZE = 20;
const VISIBILITY = ['Visible', 'Hidden'];
const RATINGS = ['5', '4', '3', '2', '1'];
const RATING_LABELS = { 5: '5 stars', 4: '4 stars', 3: '3 stars', 2: '2 stars', 1: '1 star' };

export default function AdminReviews() {
  const navigate = useNavigate();
  const [visibility, setVisibility] = useState('all');
  const [rating, setRating] = useState('all');
  const [plan, setPlan] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const q = useDebounced(search.trim());

  useEffect(() => {
    setPage(1);
  }, [visibility, rating, plan, q]);

  const { data, loading, error, run } = useFetch(() => {
    let query = sdk.table('reviews_c').select(REVIEW_FIELDS);
    if (visibility !== 'all') query = query.where(eq('hidden_c', visibility === 'Hidden'));
    if (rating !== 'all') query = query.where(eq('rating_c', Number(rating)));
    if (plan !== 'all') query = query.where(eq('plan_c', plan));
    if (q) query = query.where(or(contains('Name', q), contains('display_name_c', q), contains('body_c', q)));
    return query.orderByDesc('CreatedOn').page(page, PAGE_SIZE).fetch().then(paged);
  }, [visibility, rating, plan, q, page]);

  const list = data?.rows ?? [];
  const filtered = visibility !== 'all' || rating !== 'all' || plan !== 'all' || !!q;

  function clear() {
    setSearch('');
    setVisibility('all');
    setRating('all');
    setPlan('all');
  }

  const columns = [
    {
      label: 'Review',
      render: (r) => (
        <RowLink to={`/admin/reviews/${r.Id}`} sub={r.display_name_c}>
          {r.Name}
        </RowLink>
      ),
    },
    { label: 'Rating', render: (r) => <Stars value={r.rating_c} size={14} /> },
    { label: 'Plan', className: 'hidden md:table-cell', render: (r) => PLAN_LABELS[r.plan_c] ?? r.plan_c ?? <Dash /> },
    {
      label: 'Order',
      className: 'hidden lg:table-cell',
      render: (r) =>
        idOf(r.order_c) ? (
          <RowLink to={`/admin/orders/${idOf(r.order_c)}`} mono>
            {r.order_c?.Name ?? 'View order'}
          </RowLink>
        ) : (
          <Dash />
        ),
    },
    {
      label: 'Reply',
      className: 'hidden lg:table-cell',
      render: (r) => (r.reply_c ? <ApperIcon name="MessageSquareReply" size={16} className="text-primary" /> : <Dash />),
    },
    {
      label: 'Shown',
      render: (r) => <StatusBadge value={r.hidden_c ? 'Hidden' : 'Visible'} styles={VISIBILITY_STYLES} />,
    },
    {
      label: 'Posted',
      className: 'hidden md:table-cell text-right text-muted-foreground',
      render: (r) => formatRelativeTime(r.CreatedOn),
    },
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow="Support"
        title="Reviews"
        description="What viewers say on the storefront. Reply, hide the odd one, keep it honest."
        actions={
          <Button asChild>
            <Link to="/admin/reviews/new">
              <ApperIcon name="Plus" size={16} />
              Add review
            </Link>
          </Button>
        }
      />

      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Search headline, name or text…" />
        <FilterSelect value={visibility} onChange={setVisibility} values={VISIBILITY} allLabel="Visible and hidden" />
        <FilterSelect value={rating} onChange={setRating} values={RATINGS} labels={RATING_LABELS} allLabel="Any rating" />
        <FilterSelect value={plan} onChange={setPlan} values={PLAN_VALUES} labels={PLAN_LABELS} allLabel="All plans" />
      </Toolbar>

      <ListBody
        loading={loading}
        data={data}
        error={error}
        run={run}
        isEmpty={!list.length}
        empty={{
          icon: 'Star',
          title: filtered ? 'No reviews match' : 'No reviews yet',
          description: filtered ? 'Nothing fits these filters.' : 'Customers leave reviews from their account once a line is live.',
          action: filtered ? clear : () => navigate('/admin/reviews/new'),
          actionLabel: filtered ? 'Clear filters' : 'Add review',
        }}
      >
        <DataTable columns={columns} rows={list} />
        <Pager page={page} total={data?.total} pageSize={PAGE_SIZE} hasMore={data?.hasMore} onPage={setPage} />
      </ListBody>
    </div>
  );
}
